import React, { ReactElement } from 'react';
import { Color, ResponsiveSection, type ResponsiveSectionProps, Stack, type StackProps, Text, type TextProps } from '@stitch-fix/mode-react';
import { ContentStackJSONRichText } from '../../utils/requests/contentstack/contentStackZodSchema';
import { JsonRteRenderer } from '../JsonRteRenderer';
import { isEmpty } from '../JsonRteRenderer/JsonRteRenderer';
export type ModuleTitleProps = {
  hAlign?: StackProps['hAlign'];
  gutter?: ResponsiveSectionProps['gutter'];
  titleOrder?: 'major-first' | 'minor-first';
  major: ContentStackJSONRichText;
  title: ContentStackJSONRichText;
  subtitle: ContentStackJSONRichText;
  render?: (children: (ReactElement | null)[]) => ReactElement;
  // used as a fallback color if majorTitleTextColor or minorTitleTextColor are not provided;
  // if textColor is not provided the component default text color will be used
  textColor?: Color;
  // if provided, will set the color of the major title; if not, major title will default to textColor, if provided, or the component default text color
  majorTitleTextColor?: Color;
  // if provided, will set the color of the minor title; if not, major title will default to textColor, if provided, or the component default text color
  minorTitleTextColor?: Color;
  titleFullWidth?: boolean;
};
export const ModuleTitle = ({
  major,
  title,
  subtitle,
  hAlign = 'center',
  gutter = 'none',
  titleOrder = 'major-first',
  render,
  textColor,
  majorTitleTextColor,
  minorTitleTextColor,
  titleFullWidth = false
}: ModuleTitleProps) => {
  const textAlignment = hAlign === 'start' ? 'left' : 'center';
  const renderItems = () => {
    const items: (ReactElement | null)[] = [];

    // Build array of title parts to render
    const titleParts: {
      type: 'major' | 'minor';
      data: ContentStackJSONRichText;
      setting: TextProps['setting'];
      color: Color;
    }[] = [];
    if (titleOrder === 'major-first') {
      if (!isEmpty(major?.children)) {
        titleParts.push({
          type: 'major',
          data: major,
          setting: 'display-large',
          color: majorTitleTextColor || textColor || 'blue-45'
        });
      }
      if (!isEmpty(title?.children)) {
        titleParts.push({
          type: 'minor',
          data: title,
          setting: 'display-medium',
          color: minorTitleTextColor || textColor || 'blue-45'
        });
      }
    } else {
      if (!isEmpty(title?.children)) {
        titleParts.push({
          type: 'minor',
          data: title,
          setting: 'display-medium',
          color: minorTitleTextColor || textColor || 'blue-45'
        });
      }
      if (!isEmpty(major?.children)) {
        titleParts.push({
          type: 'major',
          data: major,
          setting: 'display-large',
          color: majorTitleTextColor || textColor || 'blue-45'
        });
      }
    }

    // Single heading wrapper with spacing between parts
    if (titleParts.length > 0) {
      items.push(<Text as="h2" spacingBottom={false} align={textAlignment} key="merged-heading">
          {titleParts.map((part, index) => <Text key={part.type} as="span" setting={part.setting} color={part.color} mb={index < titleParts.length - 1 ? 2 : undefined} style={{
          display: 'block'
        }}>
              <JsonRteRenderer data={part.data} options={{
            renderAsInline: true,
            textProps: {}
          }} />
            </Text>)}
        </Text>);
    }

    // Subtitle remains separate
    if (!isEmpty(subtitle?.children)) {
      items.push(<JsonRteRenderer key="subtitle" data={subtitle} options={{
        color: textColor || 'blue-45',
        textProps: {
          p: {
            spacingBottom: false,
            align: textAlignment
          }
        }
      }} />);
    }
    return items.map((item, index) => item && React.cloneElement(item, {
      key: item.key || index
    }));
  };
  if (render) {
    return render(renderItems());
  }
  return <ResponsiveSection mx={hAlign === 'start' ? 0 : 'auto'} mb={2.5} gutter={gutter} width={titleFullWidth ? 'xxlarge' : 'medium'}>
      <Stack hAlign={hAlign} spacing={0.5} data-testid="title-container">
        {renderItems()}
      </Stack>
    </ResponsiveSection>;
};